import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, mediaUrl } from "../lib/api";
import { toast, Toaster } from "sonner";
import { ArrowLeft, Upload, X, Star, StarOff, GripVertical } from "lucide-react";

const KIND_META = {
    events: { title: "Evento", back: "/admin/events" },
    productions: { title: "Produzione TV", back: "/admin/productions" },
    formats: { title: "Format TV", back: "/admin/formats" },
    talents: { title: "Talent", back: "/admin/talents" },
};

const EMPTY = {
    title: "",
    subtitle: "",
    description_short: "",
    description_full: "",
    slug: "",
    cover_image: "",
    images: [],
    year: "",
    category: "",
    tags: [],
    broadcaster: "",
    partner: "",
    location: "",
    status: "published",
    order: 0,
    seo_title: "",
    seo_description: "",
    extra: {},
};

export default function AdminEditor() {
    const { kind, id } = useParams();
    const navigate = useNavigate();
    const meta = KIND_META[kind];
    const isNew = id === "new";
    const [form, setForm] = useState(EMPTY);
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [tagsText, setTagsText] = useState("");
    const [extraText, setExtraText] = useState("");
    const fileRef = useRef(null);

    useEffect(() => {
        if (isNew) {
            setForm(EMPTY);
            setTagsText("");
            setExtraText("");
            return;
        }
        setLoading(true);
        api.get(`/admin/${kind}/${id}`)
            .then((r) => {
                setForm({ ...EMPTY, ...r.data });
                setTagsText((r.data.tags || []).join(", "));
                setExtraText(
                    Object.entries(r.data.extra || {})
                        .map(([k, v]) => `${k}: ${v}`)
                        .join("\n")
                );
            })
            .catch(() => toast.error("Contenuto non trovato"))
            .finally(() => setLoading(false));
    }, [kind, id, isNew]);

    const updateField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const onUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploading(true);
        try {
            const fd = new FormData();
            files.forEach((f) => fd.append("files", f));
            const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const urls = r.data.files.map((f) => f.url);
            setForm((f) => ({
                ...f,
                images: [...f.images, ...urls],
                cover_image: f.cover_image || urls[0],
            }));
            toast.success(`${urls.length} immagine/i caricata/e`);
        } catch (err) {
            toast.error("Errore upload: " + (err.response?.data?.detail || err.message));
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const removeImage = (url) => {
        setForm((f) => ({
            ...f,
            images: f.images.filter((i) => i !== url),
            cover_image: f.cover_image === url ? (f.images.find((i) => i !== url) || "") : f.cover_image,
        }));
    };

    const setCover = (url) => updateField("cover_image", url);

    const moveImage = (i, dir) => {
        setForm((f) => {
            const images = [...f.images];
            const ni = i + dir;
            if (ni < 0 || ni >= images.length) return f;
            [images[i], images[ni]] = [images[ni], images[i]];
            return { ...f, images };
        });
    };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const extra = {};
            extraText.split("\n").forEach((line) => {
                const [k, ...rest] = line.split(":");
                if (k && rest.length) extra[k.trim()] = rest.join(":").trim();
            });
            const payload = {
                ...form,
                tags: tagsText.split(",").map((s) => s.trim()).filter(Boolean),
                extra,
            };
            if (isNew) {
                const r = await api.post(`/admin/${kind}`, payload);
                toast.success("Creato");
                navigate(`/admin/${kind}/${r.data.id}`);
            } else {
                await api.put(`/admin/${kind}/${id}`, payload);
                toast.success("Salvato");
            }
        } catch (err) {
            toast.error("Errore salvataggio: " + (err.response?.data?.detail || err.message));
        } finally {
            setSaving(false);
        }
    };

    if (!meta) return <div>Sezione non valida</div>;
    if (loading) return <div className="text-white/50">Caricamento…</div>;

    return (
        <div className="max-w-5xl" data-testid={`admin-editor-${kind}`}>
            <Toaster position="top-center" richColors />
            <Link to={meta.back} className="inline-flex items-center gap-2 kicker text-white/60 hover:text-white mb-6" data-testid="editor-back">
                <ArrowLeft size={14} /> Torna a {meta.title}
            </Link>
            <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-[-0.03em] mb-10">
                {isNew ? `Nuovo ${meta.title}` : `Modifica ${meta.title}`}
            </h1>

            <form onSubmit={submit} className="space-y-10" data-testid="admin-editor-form">
                {/* CONTENT */}
                <section className="space-y-6">
                    <SectionTitle>Contenuto</SectionTitle>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <TextField label="Titolo" required value={form.title} onChange={(v) => updateField("title", v)} testId="f-title" />
                        <TextField label="Sottotitolo" value={form.subtitle} onChange={(v) => updateField("subtitle", v)} testId="f-subtitle" />
                    </div>
                    <TextAreaField
                        label="Descrizione breve"
                        rows={2}
                        value={form.description_short}
                        onChange={(v) => updateField("description_short", v)}
                        testId="f-desc-short"
                    />
                    <TextAreaField
                        label="Descrizione completa"
                        rows={8}
                        value={form.description_full}
                        onChange={(v) => updateField("description_full", v)}
                        testId="f-desc-full"
                    />
                </section>

                {/* MEDIA */}
                <section className="space-y-5" data-testid="editor-media-section">
                    <SectionTitle>Immagini</SectionTitle>
                    <div className="border border-dashed border-white/20 p-6">
                        <label className="flex flex-col items-center justify-center gap-3 cursor-pointer py-10" data-testid="upload-dropzone">
                            <Upload className="text-[color:var(--brand-blue)]" />
                            <div className="text-sm text-white/70">
                                {uploading ? "Caricamento in corso…" : "Carica immagini dal tuo dispositivo (JPG, PNG, WEBP — max 15MB)"}
                            </div>
                            <input
                                ref={fileRef}
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={onUpload}
                                disabled={uploading}
                                className="hidden"
                                data-testid="upload-input"
                            />
                            <span className="btn-primary mt-2">
                                {uploading ? "Attendere…" : "Scegli file"}
                            </span>
                        </label>
                    </div>
                    {form.images.length > 0 && (
                        <div>
                            <div className="kicker text-white/50 mb-3">
                                {form.images.length} immagine/i · la copertina è segnata con <Star size={12} className="inline text-[color:var(--brand-blue)]" />
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {form.images.map((url, i) => (
                                    <div key={url + i} className="relative group border border-white/10" data-testid={`image-tile-${i}`}>
                                        <div className="aspect-square overflow-hidden bg-neutral-900">
                                            <img src={mediaUrl(url)} alt="" className="w-full h-full object-cover" />
                                        </div>
                                        <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-black/80 backdrop-blur-sm px-2 py-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button
                                                type="button"
                                                onClick={() => setCover(url)}
                                                className="text-white/80 hover:text-[color:var(--brand-blue)]"
                                                title={form.cover_image === url ? "Copertina attuale" : "Imposta come copertina"}
                                                data-testid={`set-cover-${i}`}
                                            >
                                                {form.cover_image === url ? (
                                                    <Star size={14} className="text-[color:var(--brand-blue)] fill-[color:var(--brand-blue)]" />
                                                ) : (
                                                    <StarOff size={14} />
                                                )}
                                            </button>
                                            <div className="flex items-center gap-1">
                                                <button
                                                    type="button"
                                                    onClick={() => moveImage(i, -1)}
                                                    className="text-white/70 hover:text-white"
                                                    data-testid={`move-up-${i}`}
                                                >
                                                    <GripVertical size={14} className="rotate-90" />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => removeImage(url)}
                                                    className="text-white/80 hover:text-red-400"
                                                    data-testid={`remove-image-${i}`}
                                                >
                                                    <X size={14} />
                                                </button>
                                            </div>
                                        </div>
                                        {form.cover_image === url && (
                                            <div className="absolute top-2 left-2 bg-[color:var(--brand-blue)] text-white text-[10px] uppercase tracking-widest px-1.5 py-0.5">
                                                Copertina
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </section>

                {/* META */}
                <section className="space-y-6">
                    <SectionTitle>Metadati</SectionTitle>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <TextField label="Anno / data" value={form.year} onChange={(v) => updateField("year", v)} testId="f-year" />
                        <TextField label="Categoria" value={form.category} onChange={(v) => updateField("category", v)} testId="f-category" />
                        <TextField label="Broadcaster / Piattaforma" value={form.broadcaster} onChange={(v) => updateField("broadcaster", v)} testId="f-broadcaster" />
                        <TextField label="Partner / Sponsor / Ospite" value={form.partner} onChange={(v) => updateField("partner", v)} testId="f-partner" />
                        <TextField label="Location" value={form.location} onChange={(v) => updateField("location", v)} testId="f-location" />
                        <TextField
                            label="Ordine (asc)"
                            value={String(form.order ?? 0)}
                            onChange={(v) => updateField("order", Number(v) || 0)}
                            type="number"
                            testId="f-order"
                        />
                    </div>
                    <TextField label="Tag (separati da virgola)" value={tagsText} onChange={setTagsText} testId="f-tags" />
                    <TextAreaField
                        label="Campi extra (chiave: valore per riga — es. target: 18-34)"
                        rows={3}
                        value={extraText}
                        onChange={setExtraText}
                        testId="f-extra"
                    />
                </section>

                {/* SEO */}
                <section className="space-y-6">
                    <SectionTitle>SEO & URL</SectionTitle>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <TextField
                            label="Slug (URL)"
                            value={form.slug || ""}
                            onChange={(v) => updateField("slug", v)}
                            hint="Se vuoto, generato automaticamente dal titolo"
                            testId="f-slug"
                        />
                        <TextField label="SEO title" value={form.seo_title} onChange={(v) => updateField("seo_title", v)} testId="f-seo-title" />
                    </div>
                    <TextAreaField
                        label="SEO meta description"
                        rows={2}
                        value={form.seo_description}
                        onChange={(v) => updateField("seo_description", v)}
                        testId="f-seo-desc"
                    />
                </section>

                {/* STATUS + SAVE */}
                <section className="sticky bottom-0 bg-[#0a0a0b]/95 backdrop-blur border-t border-white/10 -mx-6 md:-mx-10 px-6 md:px-10 py-5 flex flex-wrap items-center gap-4 justify-between">
                    <div className="flex items-center gap-3">
                        <span className="kicker text-white/50">Stato:</span>
                        <div className="inline-flex border border-white/20">
                            {["published", "draft"].map((s) => (
                                <button
                                    type="button"
                                    key={s}
                                    onClick={() => updateField("status", s)}
                                    data-testid={`status-${s}`}
                                    className={`px-4 py-2 text-xs uppercase tracking-[0.18em] transition-all ${
                                        form.status === s
                                            ? "bg-[color:var(--brand-blue)] text-white"
                                            : "text-white/60 hover:text-white"
                                    }`}
                                >
                                    {s === "published" ? "Pubblicato" : "Bozza"}
                                </button>
                            ))}
                        </div>
                    </div>
                    <button type="submit" disabled={saving} className="btn-primary disabled:opacity-50" data-testid="editor-save">
                        {saving ? "Salvataggio…" : isNew ? "Crea" : "Salva modifiche"}
                    </button>
                </section>
            </form>
        </div>
    );
}

function SectionTitle({ children }) {
    return (
        <h2 className="font-display text-xl font-bold uppercase tracking-[0.16em] text-white/80 border-b border-white/10 pb-3">
            {children}
        </h2>
    );
}

function TextField({ label, value, onChange, type = "text", required, hint, testId }) {
    return (
        <label className="block">
            <span className="kicker text-white/50 block mb-2">
                {label}
                {required && <span className="text-[color:var(--brand-blue)]"> *</span>}
            </span>
            <input
                type={type}
                required={required}
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testId}
                className="w-full bg-transparent border border-white/20 focus:border-[color:var(--brand-blue)] outline-none px-3 py-2 text-base transition-colors"
            />
            {hint && <span className="text-xs text-white/40 mt-1 block">{hint}</span>}
        </label>
    );
}

function TextAreaField({ label, value, onChange, rows = 4, testId }) {
    return (
        <label className="block">
            <span className="kicker text-white/50 block mb-2">{label}</span>
            <textarea
                value={value || ""}
                rows={rows}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testId}
                className="w-full bg-transparent border border-white/20 focus:border-[color:var(--brand-blue)] outline-none px-3 py-2 text-base resize-none transition-colors"
            />
        </label>
    );
}
